import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import InputField from '../../components/common/inputField/InputField';
import styles from './ReportPage.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import reportService from '../../services/reportService';
import {
  CustomerReport,
  GSTReport,
  ReceivedAmountReport,
  InvoiceSummary,
  ReportFilters,
  InvoiceEmptyReport,
  EmptyStockTrackingReport,
  ProductStockTrackingReport,
} from '../../types/reportTypes';
import MonthYearPicker from '../../components/common/monthYearPicker/MonthYearPicker';

type TabType = 'customer' | 'gst' | 'gst-full' | 'invoice-empty' | 'empty-stock-tracking' | 'product-stock-tracking';

const ReportPage: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [activeTab, setActiveTab] = useState<TabType>('customer');
  const [loading, setLoading] = useState(false);

  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [filters, setFilters] = useState<ReportFilters>({
    startDate: '',
    endDate: '',
    customerId: '',
    search: '',
    month: getCurrentMonth(),
  });

  // Customer Report State
  const [customerData, setCustomerData] = useState<CustomerReport[]>([]);
  const [customerSummary, setCustomerSummary] = useState({
    total_customers: 0,
    total_invoice_amount: 0,
    total_paid_amount: 0,
    total_outstanding: 0,
  });

  // GST Report State
  const [gstData, setGstData] = useState<GSTReport[]>([]);
  const [gstSummary, setGstSummary] = useState({
    total_profit: 0,
    total_gst_amount: 0,
    profit_after_gst: 0,
  });

  // GST Full Report State
  const [gstFullData, setGstFullData] = useState<InvoiceSummary[]>([]);
  const [gstFullSummary, setGstFullSummary] = useState({
    total_invoices: 0,
    total_base_amount: 0,
    total_discount: 0,
    total_cgst: 0,
    total_sgst: 0,
    grand_total: 0,
  });

  // Invoice Empty Report State
  const [invoiceEmptyData, setInvoiceEmptyData] = useState<InvoiceEmptyReport[]>([]);
  const [invoiceEmptySummary, setInvoiceEmptySummary] = useState({
    total_customers: 0,
    total_qty_provided: 0,
    total_empty_returned: 0,
    total_balance_qty: 0,
  });

  // Empty Stock Tracking Report State
  const [emptyStockData, setEmptyStockData] = useState<EmptyStockTrackingReport[]>([]);
  const [emptyStockSummary, setEmptyStockSummary] = useState({
    total_transactions: 0,
    total_empty_collected: 0,
    total_empty_reduced: 0,
    current_balance: 0,
  });

  // Product Stock Tracking Report State
  const [productStockData, setProductStockData] = useState<ProductStockTrackingReport[]>([]);
  const [productStockSummary, setProductStockSummary] = useState({
    total_transactions: 0,
    total_stock_in: 0,
    total_stock_out: 0,
    current_balance: 0,
  });

  // Track if initial load is complete
  const [initialLoad, setInitialLoad] = useState(true);

  const getMonthDateRange = (month: string) => {
    const [year, monthIndex] = month.split('-').map(Number);
    const fromDate = new Date(year, monthIndex - 1, 1);
    const toDate = new Date(year, monthIndex, 0);
    const format = (d: Date) => d.toLocaleDateString('en-CA');
    return {
      fromDate: format(fromDate),
      toDate: format(toDate),
    };
  };

  const fetchCustomerReport = async () => {
    setLoading(true);
    try {
      const response = await reportService.getCustomerReport(filters);
      setCustomerData(response.data || []);
      setCustomerSummary(response.summary || {
        total_customers: 0,
        total_invoice_amount: 0,
        total_paid_amount: 0,
        total_outstanding: 0,
      });
    } catch (error) {
      console.error('Error fetching customer report:', error);
      toast.error('Failed to fetch customer report');
      setCustomerData([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchGSTReport = async () => {
    setLoading(true);
    try {
      let apiFilters = { ...filters };
      if (filters.month) {
        const { fromDate, toDate } = getMonthDateRange(filters.month);
        apiFilters = { ...apiFilters, startDate: fromDate, endDate: toDate };
      }
      const response = await reportService.getGSTReport(apiFilters);
      setGstData(response.data || []);
      setGstSummary(response.summary || {
        total_profit: 0,
        total_gst_amount: 0,
        profit_after_gst: 0,
      });
    } catch (error) {
      console.error('Error fetching GST report:', error);
      toast.error('Failed to fetch GST report');
      setGstData([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchGSTFullReport = async () => {
    setLoading(true);
    try {
      let apiFilters = { ...filters };
      if (filters.month) {
        const { fromDate, toDate } = getMonthDateRange(filters.month);
        apiFilters = { ...apiFilters, startDate: fromDate, endDate: toDate };
      }
      const response = await reportService.getGSTCustomerList(apiFilters);
      setGstFullData(response.data?.invoice_summary || []);
      setGstFullSummary(response.summary || {
        total_invoices: 0,
        total_base_amount: 0,
        total_discount: 0,
        total_cgst: 0,
        total_sgst: 0,
        grand_total: 0,
      });
    } catch (error) {
      console.error('Error fetching GST full report:', error);
      toast.error('Failed to fetch GST full report');
      setGstFullData([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchInvoiceEmptyReport = async () => {
    setLoading(true);
    try {
      let apiFilters = { ...filters };
    //   if (filters.month) {
    //     const { fromDate, toDate } = getMonthDateRange(filters.month);
    //     apiFilters = { ...apiFilters, startDate: fromDate, endDate: toDate };
    //   }
      const response = await reportService.getInvoiceEmptyReport(apiFilters);
      setInvoiceEmptyData(response.data || []);
      setInvoiceEmptySummary(response.summary || {
        total_customers: 0,
        total_qty_provided: 0,
        total_empty_returned: 0,
        total_balance_qty: 0,
      });
    } catch (error) {
      console.error('Error fetching invoice empty report:', error);
      toast.error('Failed to fetch invoice empty report');
      setInvoiceEmptyData([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchEmptyStockTrackingReport = async () => {
    setLoading(true);
    try {
      let apiFilters = { ...filters };
      if (filters.month) {
        const { fromDate, toDate } = getMonthDateRange(filters.month);
        apiFilters = { ...apiFilters, startDate: fromDate, endDate: toDate };
      }
      const response = await reportService.getEmptyStockTrackingReport(apiFilters);
      setEmptyStockData(response.data || []);
      setEmptyStockSummary(response.summary || {
        total_transactions: 0,
        total_empty_collected: 0,
        total_empty_reduced: 0,
        current_balance: 0,
      });
    } catch (error) {
      console.error('Error fetching empty stock tracking report:', error);
      toast.error('Failed to fetch empty stock tracking report');
      setEmptyStockData([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const fetchProductStockTrackingReport = async () => {
    setLoading(true);
    try {
      let apiFilters = { ...filters };
      if (filters.month) {
        const { fromDate, toDate } = getMonthDateRange(filters.month);
        apiFilters = { ...apiFilters, startDate: fromDate, endDate: toDate };
      }
      const response = await reportService.getProductStockTrackingReport(apiFilters);
      setProductStockData(response.data || []);
      setProductStockSummary(response.summary || {
        total_transactions: 0,
        total_stock_in: 0,
        total_stock_out: 0,
        current_balance: 0,
      });
    } catch (error) {
      console.error('Error fetching product stock tracking report:', error);
      toast.error('Failed to fetch product stock tracking report');
      setProductStockData([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    handleGenerateReport();
  }, [activeTab, filters.month]);

  const handleGenerateReport = () => {
    switch (activeTab) {
      case 'customer':
        fetchCustomerReport();
        break;
      case 'gst':
        fetchGSTReport();
        break;
      case 'gst-full':
        fetchGSTFullReport();
        break;
      case 'invoice-empty':
        fetchInvoiceEmptyReport();
        break;
      case 'empty-stock-tracking':
        fetchEmptyStockTrackingReport();
        break;
      case 'product-stock-tracking':
        fetchProductStockTrackingReport();
        break;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleCustomerReport = (customer: CustomerReport) => {
    window.location.href = `/reports/${customer.id}`;
  };

  const handleInvoiceEmptyDetail = (customerId: string) => {
    let url = `/reports/invoice-empty/${customerId}`;
    window.location.href = url;
  };

  const filteredCustomerData = customerData.filter((customer) => {
    if (!filters.search) return true;
    const searchText = filters.search.toLowerCase();
    return (
      customer.customer_name?.toLowerCase().includes(searchText) ||
      customer.phone?.toLowerCase().includes(searchText)
    );
  });

  const filteredInvoiceEmptyData = invoiceEmptyData.filter((item) => {
    if (!filters.search) return true;
    const searchText = filters.search.toLowerCase();
    return (
      item.customer_name?.toLowerCase().includes(searchText) ||
      item.phone?.toLowerCase().includes(searchText)
    );
  });

  const filteredEmptyStockData = emptyStockData.filter((item) => {
    if (!filters.search) return true;
    const searchText = filters.search.toLowerCase();
    return (
      item.customer_name?.toLowerCase().includes(searchText) ||
      item.invoice_number?.toLowerCase().includes(searchText)
    );
  });

  const filteredProductStockData = productStockData.filter((item) => {
    if (!filters.search) return true;
    const searchText = filters.search.toLowerCase();
    return (
      item.customer_name?.toLowerCase().includes(searchText) ||
      item.product_name?.toLowerCase().includes(searchText) ||
      item.invoice_number?.toLowerCase().includes(searchText)
    );
  });

  // ==================== RENDER FUNCTIONS ====================

  const renderCustomerReport = () => (
    <div className={styles.reportContent}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Phone</th>
              <th>Total Invoices</th>
              <th>Invoice Amount</th>
              <th>Paid Amount</th>
              <th>Outstanding</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : filteredCustomerData.length > 0 ? (
              filteredCustomerData.map((customer) => (
                <tr key={customer.id}>
                  <td>{customer.customer_name}</td>
                  <td>{customer.phone}</td>
                  <td>{customer.total_invoices}</td>
                  <td>{formatCurrency(customer.total_invoice_amount)}</td>
                  <td>{formatCurrency(customer.total_paid_amount)}</td>
                  <td>{formatCurrency(customer.outstanding_amount)}</td>
                  {role === 'admin' && (
                    <td>
                      <div className={styles.actionIcons}>
                        <Button onClick={() => handleCustomerReport(customer)} className={styles.actionButton}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderGSTReport = () => (
    <div className={styles.reportContent}>
      {!initialLoad && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📊</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Profit</div>
              <div className={styles.cardValue}>
                {formatCurrency((Number(gstSummary?.total_profit)+ Number(gstSummary?.total_gst_amount)) || 0)}
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>🏦</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total GST (18%)</div>
              <div className={styles.cardValue}>
                {formatCurrency(gstSummary?.total_gst_amount || 0)}
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>💵</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Profit After GST</div>
              <div className={styles.cardValue}>
                {formatCurrency(gstSummary?.total_profit || 0)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Month</th>
              <th>Total Sales</th>
              <th>Total Cost</th>
              <th>Profit</th>
              <th>GST Rate</th>
              <th>GST Amount</th>
              <th>Profit After GST</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : gstData.length > 0 ? (
              gstData.map((item, index) => (
                <tr key={index}>
                  <td>{item.month}</td>
                  <td>{formatCurrency(item.total_sales)}</td>
                  <td>{formatCurrency(item.total_cost)}</td>
                  <td>{formatCurrency((Number(item.profit_amount) + Number(item.gst_amount)))}</td>
                  <td>{item.gst_rate}%</td>
                  <td>{formatCurrency(item.gst_amount)}</td>
                  <td>{formatCurrency(item.profit_amount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const exportGSTFullReportToCSV = () => {
    if (gstFullData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Date', 'Invoice Number', 'Customer Name', 'Phone Number',
      'GST', 'Amount', 'CGST', 'SGST', 'Total'
    ];

    const rows = gstFullData.map(invoice => [
      formatDate(invoice.invoice_date),
      invoice.invoice_number,
      invoice.customer_name,
      invoice.phone,
      invoice.customer_gst || '-',
      invoice.base_amount.toFixed(2),
      invoice.total_cgst.toFixed(2),
      invoice.total_sgst.toFixed(2),
      invoice.grand_total.toFixed(2)
    ]);

    if (gstFullSummary) {
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Invoices', gstFullSummary.total_invoices.toString()]);
      rows.push(['Total Subtotal', Number(gstFullSummary.total_base_amount).toFixed(2)]);
      rows.push(['Total Discount', Number(gstFullSummary.total_discount).toFixed(2)]);
      rows.push(['Total CGST', Number(gstFullSummary.total_cgst).toFixed(2)]);
      rows.push(['Total SGST', Number(gstFullSummary.total_sgst).toFixed(2)]);
      rows.push(['Grand Total', Number(gstFullSummary.grand_total).toFixed(2)]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => {
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const dateStr = filters.month
      ? filters.month.replace('-', '_')
      : new Date().toISOString().split('T')[0];
    const filename = `GST_Full_Report_${dateStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported successfully');
  };

  const renderGSTFullReport = () => (
    <div className={styles.reportContent}>
      <div className={styles.exportButtonContainer} style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '16px'
      }}>
        <Button
          variant="primary"
          onClick={exportGSTFullReportToCSV}
          disabled={loading || gstFullData.length === 0}
        >
          📥 Export to CSV
        </Button>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice Number</th>
              <th>Customer Name</th>
              <th>Phone Number</th>
              <th>GST</th>
              <th>Amount</th>
              <th>CGST</th>
              <th>SGST</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : gstFullData.length > 0 ? (
              gstFullData.map((invoice, index) => (
                <tr key={index}>
                  <td>{formatDate(invoice.invoice_date)}</td>
                  <td>{invoice.invoice_number}</td>
                  <td>{invoice.customer_name}</td>
                  <td>{invoice.phone}</td>
                  <td>{invoice.customer_gst || '-'}</td>
                  <td>{formatCurrency(invoice.base_amount)}</td>
                  <td>{formatCurrency(invoice.total_cgst)}</td>
                  <td>{formatCurrency(invoice.total_sgst)}</td>
                  <td>{formatCurrency(invoice.grand_total)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ==================== INVOICE EMPTY REPORT ====================

  const exportInvoiceEmptyToCSV = () => {
    if (invoiceEmptyData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Customer Name', 'Phone', 'Total Qty Provided',
      'Empty Returned', 'Balance Qty'
    ];

    const rows = invoiceEmptyData.map(item => [
      item.customer_name,
      item.phone,
      item.total_qty_provided.toString(),
      item.total_empty_returned.toString(),
      item.balance_qty.toString()
    ]);

    if (invoiceEmptySummary) {
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Customers', invoiceEmptySummary.total_customers.toString()]);
      rows.push(['Total Qty Provided', invoiceEmptySummary.total_qty_provided.toString()]);
      rows.push(['Total Empty Returned', invoiceEmptySummary.total_empty_returned.toString()]);
      rows.push(['Total Balance Qty', invoiceEmptySummary.total_balance_qty.toString()]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => {
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const dateStr = filters.month
      ? filters.month.replace('-', '_')
      : new Date().toISOString().split('T')[0];
    const filename = `Invoice_Empty_Report_${dateStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported successfully');
  };

  const renderInvoiceEmptyReport = () => (
    <div className={styles.reportContent}>
      {!initialLoad && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>👥</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Customers</div>
              <div className={styles.cardValue}>{invoiceEmptySummary.total_customers}</div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📦</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Qty Provided</div>
              <div className={styles.cardValue}>{invoiceEmptySummary.total_qty_provided}</div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>🔄</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Empty Returned</div>
              <div className={styles.cardValue}>{invoiceEmptySummary.total_empty_returned}</div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>⚠️</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Balance Qty</div>
              <div className={styles.cardValue} style={{ color: invoiceEmptySummary.total_balance_qty > 0 ? '#dc2626' : '#059669' }}>
                {invoiceEmptySummary.total_balance_qty}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.exportButtonContainer} style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '16px'
      }}>
        <Button
          variant="primary"
          onClick={exportInvoiceEmptyToCSV}
          disabled={loading || invoiceEmptyData.length === 0}
        >
          📥 Export to CSV
        </Button>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Phone</th>
              <th>Total Qty Provided</th>
              <th>Empty Returned</th>
              <th>Balance Qty</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : filteredInvoiceEmptyData.length > 0 ? (
              filteredInvoiceEmptyData.map((item, index) => (
                <tr key={index}>
                  <td>{item.customer_name}</td>
                  <td>{item.phone}</td>
                  <td>{item.total_qty_provided}</td>
                  <td>{item.total_empty_returned}</td>
                  <td style={{
                    color: item.balance_qty > 0 ? '#dc2626' : '#059669',
                    fontWeight: 600
                  }}>
                    {item.balance_qty}
                  </td>
                  <td>
                    <div className={styles.actionIcons}>
                      <Button
                        onClick={() => handleInvoiceEmptyDetail(item.customer_id)}
                        className={styles.actionButton}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ==================== EMPTY STOCK TRACKING REPORT ====================

  const exportEmptyStockTrackingToCSV = () => {
    if (emptyStockData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Date', 'Customer Name', 'Invoice No / Purchase ID',
      'Empty Collected', 'Empty Reduced', 'Running Balance'
    ];

    const rows = emptyStockData.map(item => [
      formatDate(item.date),
      item.customer_name,
      item.invoice_number || (item.purchase_id ? `PUR-${item.purchase_id}` : '-'),
      item.empty_collected.toString(),
      item.empty_reduced.toString(),
      item.running_balance.toString()
    ]);

    if (emptyStockSummary) {
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Transactions', emptyStockSummary.total_transactions.toString()]);
      rows.push(['Total Empty Collected', emptyStockSummary.total_empty_collected.toString()]);
      rows.push(['Total Empty Reduced', emptyStockSummary.total_empty_reduced.toString()]);
      rows.push(['Current Balance', emptyStockSummary.current_balance.toString()]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => {
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const dateStr = filters.month
      ? filters.month.replace('-', '_')
      : new Date().toISOString().split('T')[0];
    const filename = `Empty_Stock_Tracking_${dateStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported successfully');
  };

  const renderEmptyStockTrackingReport = () => (
    <div className={styles.reportContent}>
      {!initialLoad && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📋</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Transactions</div>
              <div className={styles.cardValue}>{emptyStockSummary.total_transactions}</div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📥</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Empty Collected</div>
              <div className={styles.cardValue} style={{ color: '#059669' }}>
                +{emptyStockSummary.total_empty_collected}
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📤</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Empty Reduced</div>
              <div className={styles.cardValue} style={{ color: '#dc2626' }}>
                -{emptyStockSummary.total_empty_reduced}
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>⚖️</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Current Balance</div>
              <div className={styles.cardValue} style={{ 
                color: emptyStockSummary.current_balance >= 0 ? '#059669' : '#dc2626',
                fontWeight: 700
              }}>
                {emptyStockSummary.current_balance}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.exportButtonContainer} style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '16px'
      }}>
        <Button
          variant="primary"
          onClick={exportEmptyStockTrackingToCSV}
          disabled={loading || emptyStockData.length === 0}
        >
          📥 Export to CSV
        </Button>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer Name</th>
              <th>Invoice No / Purchase ID</th>
              <th>Empty Collected</th>
              <th>Empty Reduced</th>
              <th>Running Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : filteredEmptyStockData.length > 0 ? (
              <>
                {filteredEmptyStockData.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.date)}</td>
                    <td>{item.customer_name}</td>
                    <td>
                      {item.invoice_number || (item.purchase_id ? `PUR-${item.purchase_id}` : '-')}
                    </td>
                    <td style={{ color: item.empty_collected > 0 ? '#059669' : 'inherit', fontWeight: 500 }}>
                      {item.empty_collected > 0 ? `+${item.empty_collected}` : '-'}
                    </td>
                    <td style={{ color: item.empty_reduced > 0 ? '#dc2626' : 'inherit', fontWeight: 500 }}>
                      {item.empty_reduced > 0 ? `-${item.empty_reduced}` : '-'}
                    </td>
                    <td style={{ 
                      fontWeight: 600,
                      color: item.running_balance >= 0 ? '#059669' : '#dc2626'
                    }}>
                      {item.running_balance}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 'bold' }}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>Total</td>
                  <td style={{ color: '#059669' }}>
                    +{emptyStockSummary.total_empty_collected}
                  </td>
                  <td style={{ color: '#dc2626' }}>
                    -{emptyStockSummary.total_empty_reduced}
                  </td>
                  <td style={{ 
                    color: emptyStockSummary.current_balance >= 0 ? '#059669' : '#dc2626'
                  }}>
                    {emptyStockSummary.current_balance}
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ==================== PRODUCT STOCK TRACKING REPORT ====================

  const exportProductStockTrackingToCSV = () => {
    if (productStockData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = [
      'Date', 'Customer Name', 'Invoice No / Purchase ID', 'Product Name',
      'Stock In', 'Stock Out', 'Running Balance'
    ];

    const rows = productStockData.map(item => [
      formatDate(item.date),
      item.customer_name,
      item.invoice_number || (item.purchase_id ? `PUR-${item.purchase_id}` : '-'),
      item.product_name,
      item.stock_in.toString(),
      item.stock_out.toString(),
      item.running_balance.toString()
    ]);

    if (productStockSummary) {
      rows.push([]);
      rows.push(['Summary']);
      rows.push(['Total Transactions', productStockSummary.total_transactions.toString()]);
      rows.push(['Total Stock In', productStockSummary.total_stock_in.toString()]);
      rows.push(['Total Stock Out', productStockSummary.total_stock_out.toString()]);
      rows.push(['Current Balance', productStockSummary.current_balance.toString()]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => {
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const dateStr = filters.month
      ? filters.month.replace('-', '_')
      : new Date().toISOString().split('T')[0];
    const filename = `Product_Stock_Tracking_${dateStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Report exported successfully');
  };

  const renderProductStockTrackingReport = () => (
    <div className={styles.reportContent}>
      {!initialLoad && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📋</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Transactions</div>
              <div className={styles.cardValue}>{productStockSummary.total_transactions}</div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📥</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Stock In</div>
              <div className={styles.cardValue} style={{ color: '#059669' }}>
                +{productStockSummary.total_stock_in}
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>📤</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Stock Out</div>
              <div className={styles.cardValue} style={{ color: '#dc2626' }}>
                -{productStockSummary.total_stock_out}
              </div>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>⚖️</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Current Balance</div>
              <div className={styles.cardValue} style={{ 
                color: productStockSummary.current_balance >= 0 ? '#059669' : '#dc2626',
                fontWeight: 700
              }}>
                {productStockSummary.current_balance}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.exportButtonContainer} style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '16px'
      }}>
        <Button
          variant="primary"
          onClick={exportProductStockTrackingToCSV}
          disabled={loading || productStockData.length === 0}
        >
          📥 Export to CSV
        </Button>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer Name</th>
              <th>Invoice No / Purchase ID</th>
              <th>Product Name</th>
              <th>Stock In</th>
              <th>Stock Out</th>
              <th>Running Balance</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : filteredProductStockData.length > 0 ? (
              <>
                {filteredProductStockData.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.date)}</td>
                    <td>{item.customer_name}</td>
                    <td>
                      {item.invoice_number || (item.purchase_id ? `PUR-${item.purchase_id}` : '-')}
                    </td>
                    <td>{item.product_name}</td>
                    <td style={{ color: item.stock_in > 0 ? '#059669' : 'inherit', fontWeight: 500 }}>
                      {item.stock_in > 0 ? `+${item.stock_in}` : '-'}
                    </td>
                    <td style={{ color: item.stock_out > 0 ? '#dc2626' : 'inherit', fontWeight: 500 }}>
                      {item.stock_out > 0 ? `-${item.stock_out}` : '-'}
                    </td>
                    <td style={{ 
                      fontWeight: 600,
                      color: item.running_balance >= 0 ? '#059669' : '#dc2626'
                    }}>
                      {item.running_balance}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 'bold' }}>
                  <td colSpan={4} style={{ textAlign: 'right' }}>Total</td>
                  <td style={{ color: '#059669' }}>
                    +{productStockSummary.total_stock_in}
                  </td>
                  <td style={{ color: '#dc2626' }}>
                    -{productStockSummary.total_stock_out}
                  </td>
                  <td style={{ 
                    color: productStockSummary.current_balance >= 0 ? '#059669' : '#dc2626'
                  }}>
                    {productStockSummary.current_balance}
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <h4>Reports</h4>

      <div className={styles.filterSection}>
        <div className={styles.filterInputs}>
          {(activeTab === 'gst' || activeTab === 'gst-full' || activeTab === 'empty-stock-tracking' || activeTab === 'product-stock-tracking') && (
            <MonthYearPicker
              label="Select Month and Year"
              name="month"
              value={filters.month ?? ''}
              onChange={(value) => setFilters({ ...filters, month: value })}
            />
          )}
          {(activeTab === 'customer' || activeTab === 'invoice-empty' || activeTab === 'empty-stock-tracking' || activeTab === 'product-stock-tracking') && (
            <InputField
              label={activeTab === 'product-stock-tracking' ? "Search Product/Customer" : "Search Customer"}
              type="text"
              name="search"
              value={filters.search ?? ''}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder={activeTab === 'product-stock-tracking' ? "Search by product or customer" : "Search by name or phone"}
            />
          )}
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'customer' ? styles.active : ''}`}
          onClick={() => setActiveTab('customer')}
        >
          Customer Report
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'gst' ? styles.active : ''}`}
          onClick={() => setActiveTab('gst')}
        >
          GST Report
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'gst-full' ? styles.active : ''}`}
          onClick={() => setActiveTab('gst-full')}
        >
          GST Full Report
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'invoice-empty' ? styles.active : ''}`}
          onClick={() => setActiveTab('invoice-empty')}
        >
          Invoice Empty Report
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'empty-stock-tracking' ? styles.active : ''}`}
          onClick={() => setActiveTab('empty-stock-tracking')}
        >
          Empty Stock Tracking
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'product-stock-tracking' ? styles.active : ''}`}
          onClick={() => setActiveTab('product-stock-tracking')}
        >
          Product Stock Tracking
        </button>
      </div>

      {activeTab === 'customer' && renderCustomerReport()}
      {activeTab === 'gst' && renderGSTReport()}
      {activeTab === 'gst-full' && renderGSTFullReport()}
      {activeTab === 'invoice-empty' && renderInvoiceEmptyReport()}
      {activeTab === 'empty-stock-tracking' && renderEmptyStockTrackingReport()}
      {activeTab === 'product-stock-tracking' && renderProductStockTrackingReport()}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default ReportPage;